import { orderTokenHeaders } from '@/utils/global/orderTokenHeaders'
import axios from 'axios'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'

import { api } from '@/utils/global/api'
import { orderProductsSync } from '@/utils/global/orderProductsSync'

/**
 * Axios instance for API requests
 *
 * @description
 * - Uses base URL from global api object
 * - Includes request/response interceptors
 * - Handles authentication headers automatically
 *
 * @example
 * // Component
 * const MyComponent = () => {
 *   await archive(selectedLayout.id)
 * }
 *
 * // API function
 * export const archive = async (pagesetId: string) => {
 *   const response = await axios.patch(
 *     `archivePageSet/${pagesetId}`,
 *     {},
 *   )
 *   return response
 * }
 */
const instance: AxiosInstance = axios.create({ baseURL: api })

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    config.headers = config.headers || {}

    // A request that carries its own JWT sends no edit-link headers, and
    // `orderTokenHeaders()` sends none while the client holds one: beside a
    // JWT the backend judges the request as the edit link of the order.
    if (!config.headers['Authorization'])
      Object.assign(config.headers, orderTokenHeaders())

    return config
  },
  (error: AxiosError) => {
    console.error('Axios Request Error', error)
    return Promise.reject(error)
  },
)

// Theme/language stale-data signal: the backend flags a response (success OR
// rejection) when the client's order data is out of date — a healed page-set
// create (200 + healed body flag) or a rejected cross-theme template pick
// (400). Broadcast so subscribers owning that order refetch it — the backend
// is the source of truth. Broadcasts for the same order are coalesced so an
// editor load that heals N page sets triggers one refetch, not N.
const STALE_BROADCAST_WINDOW_MS = 1000
const lastStaleBroadcastAt = new Map<string, number>()
const pendingStaleBroadcast = new Map<string, ReturnType<typeof setTimeout>>()

// Leading + trailing coalescing per order: the first signal broadcasts
// immediately; further signals inside the window schedule ONE deferred
// broadcast at the window's end, so a mutation that lands after the first
// refetch started is never lost, while an editor load that heals N page
// sets still triggers at most two refetches.
const broadcastStaleOrder = (
  headers: AxiosResponse['headers'] | undefined,
  data: unknown,
): void => {
  const body = data as
    | { stale_order_id?: unknown; healed?: unknown }
    | undefined
  const staleOrderId =
    headers?.['x-order-data-stale'] ||
    (body?.healed && body?.stale_order_id) ||
    body?.stale_order_id
  if (!staleOrderId) return
  const key = String(staleOrderId)
  const now = Date.now()
  const last = lastStaleBroadcastAt.get(key) ?? 0
  const remaining = STALE_BROADCAST_WINDOW_MS - (now - last)
  if (remaining > 0) {
    if (!pendingStaleBroadcast.has(key)) {
      pendingStaleBroadcast.set(
        key,
        setTimeout(() => {
          pendingStaleBroadcast.delete(key)
          lastStaleBroadcastAt.set(key, Date.now())
          orderProductsSync.notify({ staleOrderId: key })
        }, remaining),
      )
    }
    return
  }
  lastStaleBroadcastAt.set(key, now)
  orderProductsSync.notify({ staleOrderId: key })
}

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    broadcastStaleOrder(response.headers, response.data)
    return response
  },
  (error: AxiosError) => {
    // Rejections carry the stale flag too (e.g. a 400 from picking a template
    // that no longer matches the line's theme) — without this the client
    // would keep retrying with the same stale data.
    broadcastStaleOrder(error.response?.headers, error.response?.data)
    console.error('Axios Response Error', error)
    return Promise.reject(error)
  },
)

instance.defaults.withCredentials = false

export { instance as axios }
export default instance
